import * as yup from 'yup'
import { addHours, isAfter, isBefore, isSameDay } from 'date-fns'
import { SlotStatus } from '../../../models/DistributionPlan'

export interface DistributionFormContext {
  canEditStartTimestamp: boolean
  canEditEndTimestamp: boolean
  canEditReleaseTimestamp: boolean
  slotStatus: SlotStatus | undefined
  lastChangedField: 'start' | 'end' | 'release' | null
}

export interface CourseSectionFormContext {
  canEditStartTimestamp: boolean
  canEditEndDate: boolean
  canEditReleaseDates: boolean
  isRollingRelease: boolean
  slotReleaseTimestamp: string | undefined
  lastChangedField: 'start' | 'end' | 'earlyAccess1' | 'earlyAccess2' | null
}
const isNowOrFuture = (label: string): yup.DateSchema<any> =>
  yup
    .date()
    .typeError(`${label} is invalid`)
    .test(
      'future-date',
      `${label} must be now or in the future`,
      (value) => !value || value.getTime() >= Date.now()
    )

export const distributionFormSchema = yup.object().shape({
  termId: yup.string().required('Term is required'),
  templateId: yup.string().required('Survey is required'),

  startTimestamp: yup
    .date()
    .typeError('Start Date is invalid')
    .when('$canEditStartTimestamp', {
      is: true,
      then: (schema) =>
        schema.test(
          'future-date',
          'Start date must be now or in the future',
          function (value) {
            return !value || new Date(value).getTime() >= Date.now()
          }
        ),
      otherwise: (schema) => schema.notRequired()
    })
    .test('before-end', 'Start Date must be before End Date', function (value) {
      const { lastChangedField, canEditStartTimestamp } = this.options.context ?? {}
      if (!canEditStartTimestamp) return true
      if (lastChangedField !== 'start' && lastChangedField !== null) return true
      const { endTimestamp } = this.parent
      if (!value || !endTimestamp) return true
      return isBefore(new Date(value), new Date(endTimestamp))
    })
    .test('24h-before-end', 'End Date must be at least 24 hours after Start Date', function (value) {
      const { lastChangedField, canEditStartTimestamp } = this.options.context ?? {}
      if (!canEditStartTimestamp) return true
      if (lastChangedField !== 'start' && lastChangedField !== null) return true
      const { endTimestamp } = this.parent
      if (!value || !endTimestamp) return true
      return isAfter(new Date(endTimestamp), addHours(new Date(value), 24))
    }),

  endTimestamp: yup
    .date()
    .when('$canEditEndTimestamp', {
      is: true,
      then: (schema) =>
        schema
          .required('Default End Date is required')
          .test('future-date', 'End Date must be in the future', (value) => {
            if (!value) return true
            return isAfter(new Date(value), new Date())
          }),
      otherwise: (schema) => schema.notRequired()
    })
    .test('after-start', 'End Date must be after Start Date', function (value) {
      const { lastChangedField, canEditEndTimestamp, slotStatus } = this.options.context ?? {}
      if (!canEditEndTimestamp) return true
      if (lastChangedField !== 'end' && lastChangedField !== null) return true
      const { startTimestamp } = this.parent
      if (!value || !startTimestamp) return true
      // In Live state start has already passed — validating end > start would always fail against the stored past date
      if (slotStatus === SlotStatus.Live) return true
      return isAfter(new Date(value), new Date(startTimestamp))
    })
    .test(
      'is-24-hours-later',
      'End Date must be at least 24 hours after Start Date',
      function (endTimestamp) {
        const { lastChangedField, canEditEndTimestamp, slotStatus } = this.options.context ?? {}
        if (!canEditEndTimestamp) return true
        if (lastChangedField !== 'end' && lastChangedField !== null) return true
        const { startTimestamp } = this.parent
        // In Live state start has already passed — skip the 24h gap check against the stored past date
        if (!startTimestamp || !endTimestamp || slotStatus === SlotStatus.Live) return true
        return isAfter(new Date(endTimestamp), addHours(new Date(startTimestamp), 24))
      }
    )
    .test('before-release', 'End Date must be on or before Release Date', function (value) {
      const { lastChangedField, canEditEndTimestamp } = this.options.context ?? {}
      if (!canEditEndTimestamp) return true
      if (lastChangedField !== 'end' && lastChangedField !== null) return true
      const { releaseTimestamp } = this.parent
      if (!value || !releaseTimestamp) return true
      const endDate = new Date(value)
      const releaseDate = new Date(releaseTimestamp)
      return isAfter(releaseDate, endDate) || isSameDay(releaseDate, endDate)
    }),

  releaseTimestamp: yup
    .date()
    .transform((value, originalValue) => {
      if (originalValue === 'Rolling Release') {
        return new Date('3000-01-01T00:00:00Z')
      }
      const parsed = new Date(originalValue)
      return isNaN(parsed.getTime()) ? null : parsed
    })
    .when('$canEditReleaseTimestamp', {
      is: true,
      then: (schema) =>
        schema.test('future-date', 'Release Date must be in the future', (value) => {
          if (!value) return true
          return isAfter(new Date(value), new Date())
        }),
      otherwise: (schema) => schema
    })
    .test('after-end', 'Release Date must be on or after End Date', function (value) {
      const { lastChangedField, canEditReleaseTimestamp } = this.options.context ?? {}
      if (!canEditReleaseTimestamp) return true
      if (lastChangedField !== 'release' && lastChangedField !== null) return true
      const { endTimestamp } = this.parent
      if (!value || !endTimestamp) return true
      const releaseDate = new Date(value)
      const endDate = new Date(endTimestamp)
      return isAfter(releaseDate, endDate) || isSameDay(releaseDate, endDate)
    })
    .when(['isRollingResultsEnabled', 'slotStatus'], {
      is: (isRolling: boolean, status: string) => !isRolling && status !== SlotStatus.Closed,
      then: (schema) => schema.required('Release Date is required'),
      otherwise: (schema) => schema.strip()
    }),

  isAcademicLevelAddedQuestionsAllowed: yup.boolean().notRequired(),
  isInstructorAddedQuestionsAllowed: yup.boolean().notRequired(),
  isRollingResultsEnabled: yup.boolean().notRequired(),

  academicLevelAddedQuestions: yup
    .object({
      academicUnitCategories: yup.array().nullable(),
      questionCount: yup.number().nullable(),
      isCustomQuestionAllowed: yup.boolean().notRequired()
    })
    .test('academic-questions-validation', '', function (value) {
      const { isAcademicLevelAddedQuestionsAllowed } = this.parent
      if (!isAcademicLevelAddedQuestionsAllowed) return true

      if (!value?.academicUnitCategories || value.academicUnitCategories.length === 0) {
        return this.createError({
          path: 'academicLevelAddedQuestions.academicUnitCategories',
          message: 'At least one Academic Unit level is required'
        })
      }

      if (value.questionCount === undefined || value.questionCount === null || value.questionCount < 1) {
        return this.createError({
          path: 'academicLevelAddedQuestions.questionCount',
          message: 'Number of questions is required and must be at least 1'
        })
      }

      return true
    }),

  instructorAddedQuestions: yup
    .object({
      questionCount: yup.number().nullable(),
      isCustomQuestionAllowed: yup.boolean().notRequired()
    })
    .test('instructor-questions-validation', '', function (value) {
      const { isInstructorAddedQuestionsAllowed } = this.parent

      if (!isInstructorAddedQuestionsAllowed) return true

      // Same carve-out as the section-added sibling below, for the same reason: the limit cannot be
      // supplied outside Draft, so requiring it there locks the whole modal on legacy rows.
      const { slotStatus } = this.options.context ?? {}
      const canSupplyLimit = slotStatus === undefined || slotStatus === SlotStatus.Draft
      if (!canSupplyLimit && (!value || value.questionCount == null)) return true

      if (!value) {
        return this.createError({
          path: 'instructorAddedQuestions.questionCount',
          message: 'Number of questions is required'
        })
      }

      if (typeof value.questionCount !== 'number' || value.questionCount < 1) {
        return this.createError({
          path: 'instructorAddedQuestions.questionCount',
          message: 'Number of questions must be at least 1'
        })
      }

      return true
    }),

  // Section-added questions (CR-295): one shared set per course section, as opposed to
  // instructorAddedQuestions which repeats per instructor. Same validation shape as its
  // sibling above — deliberately, so the two blocks behave identically in the modal.
  sectionAddedQuestions: yup
    .object({
      questionCount: yup.number().nullable(),
      isCustomQuestionAllowed: yup.boolean().notRequired()
    })
    .test('section-questions-validation', '', function (value) {
      const { isSectionAddedQuestionsAllowed } = this.parent

      if (!isSectionAddedQuestionsAllowed) return true

      // Required again, and identical to its instructor-added sibling. It was briefly optional
      // because `apply_default` invented the value from the template's question count; the backend
      // withdrew that — a limit is now step 2 of enabling the type, and attaching a template
      // without one is a 422. So an absent limit is not a state worth letting through Save.
      //
      // Except where the admin cannot supply one. The input is disabled outside Draft, so on a
      // project enabled under the old one-call flow — type on, no limit recorded — this pinned an
      // unfixable error to a field they cannot edit and blocked Save for everything else in the
      // modal: term, survey dates, release dates, opt-out. A rule nobody can satisfy is not a rule,
      // it is a lock. Found by review.
      const { slotStatus } = this.options.context ?? {}
      const canSupplyLimit = slotStatus === undefined || slotStatus === SlotStatus.Draft
      if (!canSupplyLimit && (!value || value.questionCount == null)) return true

      if (!value || value.questionCount == null) {
        return this.createError({
          path: 'sectionAddedQuestions.questionCount',
          message: 'Number of questions is required'
        })
      }

      if (typeof value.questionCount !== 'number' || value.questionCount < 1) {
        return this.createError({
          path: 'sectionAddedQuestions.questionCount',
          message: 'Number of questions must be at least 1'
        })
      }

      return true
    })
})

export const courseSectionSchema = yup.object({
  sectionId: yup
    .string()
    .required('Section ID is required'),

  startTimestamp: yup
    .date()
    .typeError('Start Date is invalid')
    .when('$canEditStartTimestamp', {
      is: true,
      then: (schema) =>
        schema.test(
          'future-date',
          'Start date must be now or in the future',
          (value) => !value || new Date(value).getTime() >= Date.now()
        ),
      otherwise: (schema) => schema.notRequired()
    })
    .test('before-end', 'Start Date must be before End Date', function (value) {
      const { lastChangedField, canEditStartTimestamp } = this.options.context ?? {}
      if (!canEditStartTimestamp) return true
      if (lastChangedField !== 'start' && lastChangedField !== null) return true
      const { endTimestamp } = this.parent
      if (!value || !endTimestamp) return true
      return new Date(value).getTime() < new Date(endTimestamp).getTime()
    }),

  endTimestamp: yup
    .date()
    .typeError('End Date is invalid')
    .when('$canEditEndDate', {
      is: true,
      then: (schema) =>
        schema
          .required('End Date is required')
          .test(
            'future-date',
            'End date must be now or in the future',
            (value) => !value || new Date(value).getTime() >= Date.now()
          ),
      otherwise: (schema) => schema.notRequired()
    })
    .test('after-start', 'End Date must be after Start Date', function (value) {
      const { lastChangedField, canEditEndDate } = this.options.context ?? {}
      if (!canEditEndDate) return true
      if (lastChangedField !== 'end' && lastChangedField !== null) return true
      const { startTimestamp } = this.parent
      if (!value || !startTimestamp) return true
      return new Date(value).getTime() > new Date(startTimestamp).getTime()
    })
    .test('before-earlyAccess1', 'End Date must be on or before Instructor Release Date', function (value) {
      const { lastChangedField, canEditEndDate } = this.options.context ?? {}
      if (!canEditEndDate) return true
      if (lastChangedField !== 'end' && lastChangedField !== null) return true
      const { earlyAccessReleaseTimestamp1 } = this.parent
      if (!value || !earlyAccessReleaseTimestamp1) return true
      return new Date(value).getTime() <= new Date(earlyAccessReleaseTimestamp1).getTime()
    })
    .test('before-earlyAccess2', 'End Date must be on or before Admin Release Date', function (value) {
      const { lastChangedField, canEditEndDate } = this.options.context ?? {}
      if (!canEditEndDate) return true
      if (lastChangedField !== 'end' && lastChangedField !== null) return true
      const { earlyAccessReleaseTimestamp2 } = this.parent
      if (!value || !earlyAccessReleaseTimestamp2) return true
      return new Date(value).getTime() <= new Date(earlyAccessReleaseTimestamp2).getTime()
    }),

  earlyAccessReleaseTimestamp1: yup
    .date()
    .typeError('Instructor Release Date is invalid')
    .nullable()
    .when('$isRollingRelease', {
      is: true,
      then: (schema) => schema.strip(),
      otherwise: (schema) =>
        schema
          .nullable()
          .test('future-date', 'Instructor Release Date must be now or in the future', function (value) {
            const { canEditReleaseDates } = this.options.context ?? {}
            if (!canEditReleaseDates) return true
            if (!value) return true
            return new Date(value).getTime() >= Date.now()
          })
          .test('after-end', 'Instructor Release Date must be after End Date', function (value) {
            const { lastChangedField, canEditReleaseDates } = this.options.context ?? {}
            if (!canEditReleaseDates) return true
            if (lastChangedField !== 'earlyAccess1' && lastChangedField !== null) return true
            if (!value) return true
            const end = this.resolve(yup.ref('endTimestamp'))
            if (
              !end ||
              (!(end instanceof Date) && typeof end !== 'string' && typeof end !== 'number')
            ) { return true }
            return new Date(value).getTime() >= new Date(end).getTime()
          })
          .test('before-slot-release', 'Instructor Release Date must be on or before the project release date', function (value) {
            if (!value) return true
            const { slotReleaseTimestamp, canEditReleaseDates } = this.options.context ?? {}
            if (!canEditReleaseDates || !slotReleaseTimestamp) return true
            return new Date(value).getTime() <= new Date(slotReleaseTimestamp).getTime()
          })
    }),

  earlyAccessReleaseTimestamp2: yup
    .date()
    .typeError('Admin Release Date is invalid')
    .nullable()
    .when('$isRollingRelease', {
      is: true,
      then: (schema) => schema.strip(),
      otherwise: (schema) =>
        schema
          .nullable()
          .test('future-date', 'Admin Release Date must be now or in the future', function (value) {
            const { canEditReleaseDates } = this.options.context ?? {}
            if (!canEditReleaseDates) return true
            if (!value) return true
            return new Date(value).getTime() >= Date.now()
          })
          .test('after-end', 'Admin Release Date must be after End Date', function (value) {
            const { lastChangedField, canEditReleaseDates } = this.options.context ?? {}
            if (!canEditReleaseDates) return true
            if (lastChangedField !== 'earlyAccess2' && lastChangedField !== null) return true
            if (!value) return true
            const end = this.resolve(yup.ref('endTimestamp'))
            if (
              !end ||
              (!(end instanceof Date) && typeof end !== 'string' && typeof end !== 'number')
            ) { return true }
            return new Date(value).getTime() >= new Date(end).getTime()
          })
          .test('before-slot-release', 'Admin Release Date must be on or before the project release date', function (value) {
            if (!value) return true
            const { slotReleaseTimestamp, canEditReleaseDates } = this.options.context ?? {}
            if (!canEditReleaseDates || !slotReleaseTimestamp) return true
            return new Date(value).getTime() <= new Date(slotReleaseTimestamp).getTime()
          })
    })
})
